import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, LogOut, User } from 'lucide-react';
import shiftApi, { Shift } from '../services/shiftApi';
import AttendanceLogoffModal from './AttendanceLogoffModal';
import { useAuth } from '../contexts/AuthContext';

interface ActiveAttendanceListProps {
  canMarkAttendanceForOthers: boolean;
}

const ActiveAttendanceList: React.FC<ActiveAttendanceListProps> = ({
  canMarkAttendanceForOthers
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [logoffShift, setLogoffShift] = useState<Shift | null>(null);

  // Get active attendances
  const { data: activeAttendancesData, isLoading } = useQuery({
    queryKey: ['active-attendances'],
    queryFn: () => shiftApi.getActiveAttendances(),
    refetchInterval: 30000 // Refetch every 30 seconds
  });

  const activeShifts: Shift[] = activeAttendancesData?.shifts || [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (start: string) => {
    const startTime = new Date(start).getTime();
    const now = Date.now();
    const hours = Math.floor((now - startTime) / (1000 * 60 * 60));
    const minutes = Math.floor(((now - startTime) % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const handleLogoff = (shift: Shift) => {
    setLogoffShift(shift);
  };

  const handleLogoffSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['active-attendances'] });
    setLogoffShift(null);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Active Attendances
          </h2>
        </div>

        {activeShifts.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500">No active attendances found.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {activeShifts.map((shift) => {
              const canLogoffThisShift = canMarkAttendanceForOthers || shift.employee.user.id === user?.id;
              const machineGroups = shift.machines
                .filter(m => m.machineGroup && !m.unassignedAt)
                .map(m => m.machineGroup?.name)
                .filter(Boolean);

              return (
                <div key={shift.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            {shift.employee.user.name}
                          </h3>
                          <p className="text-sm text-gray-500">{shift.employee.user.email}</p>
                        </div>
                      </div>

                      <div className="ml-13 space-y-1">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>Started: {formatDate(shift.startedAt)}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          <span>Duration: {formatDuration(shift.startedAt)}</span>
                        </div>
                        {machineGroups.length > 0 && (
                          <div className="mt-2">
                            <span className="text-sm font-medium text-gray-700">Machine Groups: </span>
                            <span className="text-sm text-gray-600">
                              {machineGroups.join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {canLogoffThisShift && (
                      <button
                        onClick={() => handleLogoff(shift)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        Logoff
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {logoffShift && (
        <AttendanceLogoffModal
          shift={logoffShift}
          onClose={() => setLogoffShift(null)}
          onLogoffSuccess={handleLogoffSuccess}
          canMarkAttendanceForOthers={canMarkAttendanceForOthers}
        />
      )}
    </>
  );
};

export default ActiveAttendanceList;

